import { AssistChip, Box, Toast, useToast } from '@zillow/constellation-10';
import { IconMailFilled, IconPhoneFilled } from '@zillow/constellation-icons';
import { useContext } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ModalType } from '../../../../types/ModalType';
import {
  CONTACT_INFO_CALL_ICON_CLICK_EVENT_TYPE_ID,
  CONTACT_INFO_COPY_EMAIL_CLICK_EVENT_TYPE_ID,
  emitContactInfoCallIconClickEvent,
  emitContactInfoCopyEmailClickEvent,
} from '../../../../constants/clickstreamEvents/actionSidebar';
import useModal from '../../../../hooks/useModal';
import { useConversationMessagesQuery } from '../../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import ContactInfoCallModal from '../../../Modals/ContactInfoCallModal';
import { useAuth } from '../../../../AuthProvider';

export type ContactData = { name: string; phoneNumber: string; email: string };

export const selectContactData = (data: ConversationMessagesData): ContactData => ({
  name: data.renterName ?? '',
  phoneNumber: data.renterPhone ?? '',
  email: data.renterEmail ?? '',
});

export default function ContactActions(): JSX.Element | null {
  const { conversationId, clientTimeZone } = useContext(InboxPageContext);
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: contactData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectContactData,
  });
  const { activeModal, setActiveModal } = useModal();

  if (!contactData) {
    return null;
  }

  const { name, phoneNumber, email } = contactData;
  const shouldShowCallButton = Boolean(phoneNumber);
  const shouldShowEmailButton = Boolean(email);

  const handleContactInfoCall = () => {
    setActiveModal(ModalType.ContactInfoCallModal);
    emitContactInfoCallIconClickEvent(user);
  };

  const handleCopyEmailClick = () => {
    enqueueToast(<Toast tone="success">Email copied</Toast>);
    emitContactInfoCopyEmailClickEvent(user);
  };

  return (
    <>
      {activeModal === ModalType.ContactInfoCallModal && <ContactInfoCallModal phone={phoneNumber} name={name} />}

      <Box
        css={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', justifyContent: 'center', gap: 'tight' }}
      >
        {shouldShowCallButton && (
          <AssistChip
            data-eventid={CONTACT_INFO_CALL_ICON_CLICK_EVENT_TYPE_ID}
            aria-haspopup="dialog"
            onClick={handleContactInfoCall}
            icon={<IconPhoneFilled />}
          >
            Call
          </AssistChip>
        )}

        {shouldShowEmailButton && (
          <CopyToClipboard onCopy={handleCopyEmailClick} text={email}>
            <AssistChip
              data-eventid={CONTACT_INFO_COPY_EMAIL_CLICK_EVENT_TYPE_ID}
              aria-haspopup="dialog"
              icon={<IconMailFilled />}
            >
              Email
            </AssistChip>
          </CopyToClipboard>
        )}
      </Box>
    </>
  );
}
